import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IDuelRound, IDuelRoundPlayer, IDuelRoundAbility } from '@pvplevelup/core/dist/interfaces/IDuel';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { classImages } from 'utilities/ClassImages';
import { getAbility } from 'utilities/ability_icons';
import { TeamColors } from 'utilities/team_colors';

const MUTED = 'rgba(255, 255, 255, 0.6)';
const DIVIDER = '1px solid rgba(255, 255, 255, 0.07)';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(26, 27, 32, 0.95), rgba(18, 18, 22, 0.95))',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    boxShadow: '0 10px 28px rgba(0, 0, 0, 0.28)',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridGap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: DIVIDER,
    background: 'rgba(255, 255, 255, 0.02)',
  },
  roundNumber: {
    padding: '3px 10px',
    borderRadius: 8,
    fontSize: '0.75rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#90caf9',
    background: 'rgba(66, 165, 245, 0.14)',
    border: '1px solid rgba(66, 165, 245, 0.3)',
  },
  result: {
    fontSize: '0.9rem',
    color: MUTED,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& b': {
      color: '#fff',
    },
  },
  duration: {
    fontSize: '0.85rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: MUTED,
  },
  players: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  },
  player: {
    padding: theme.spacing(1.75, 2),
    minWidth: 0,
    '& + &': {
      borderLeft: DIVIDER,
    },
  },
  playerHead: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gridGap: 10,
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  classIcon: {
    width: 34,
    height: 34,
    display: 'block',
    borderRadius: 8,
  },
  playerName: {
    fontSize: '1rem',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  playerClass: {
    fontSize: '0.75rem',
    color: MUTED,
  },
  wonTag: {
    padding: '2px 8px',
    borderRadius: 8,
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#a5d6a7',
    background: 'rgba(102, 187, 106, 0.14)',
    border: '1px solid rgba(102, 187, 106, 0.35)',
  },
  totals: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gridGap: 6,
    marginBottom: theme.spacing(1.5),
  },
  total: {
    padding: theme.spacing(0.75, 0.5),
    borderRadius: 8,
    textAlign: 'center',
    background: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },
  totalValue: {
    fontSize: '0.98rem',
    fontWeight: 800,
    fontVariantNumeric: 'tabular-nums',
  },
  totalLabel: {
    marginTop: 1,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  sectionTitle: {
    margin: theme.spacing(1.25, 0, 0.5),
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  ability: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '22px minmax(0, 1fr) auto',
    gridGap: 8,
    alignItems: 'center',
    padding: '3px 8px 3px 3px',
    marginBottom: 2,
    borderRadius: 6,
    overflow: 'hidden',
    fontSize: '0.82rem',
    background: 'rgba(255, 255, 255, 0.025)',
  },
  abilityBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    opacity: 0.22,
    borderRadius: 6,
  },
  abilityIcon: {
    position: 'relative',
    width: 22,
    height: 22,
    display: 'block',
    borderRadius: 4,
  },
  abilityName: {
    position: 'relative',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  abilityCount: {
    color: MUTED,
    marginLeft: 4,
  },
  abilityValue: {
    position: 'relative',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    fontWeight: 600,
  },
  abilityPct: {
    marginLeft: 6,
    fontWeight: 400,
    color: MUTED,
  },
  none: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.4)',
  },
  ccs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, max-content))',
    gridGap: 6,
  },
  cc: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 6,
    alignItems: 'center',
    justifyContent: 'start',
    padding: '2px 8px',
    borderRadius: 8,
    fontSize: '0.78rem',
    background: 'rgba(238, 241, 9, 0.07)',
    border: '1px solid rgba(238, 241, 9, 0.2)',
    '& b': {
      color: '#eef109',
    },
  },
}));

export function formatDuration(seconds: number | null | undefined){
  let total = Math.max(0, Math.round(seconds || 0));
  let minutes = Math.floor(total / 60);
  let secs = total % 60;
  return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
}

const AbilityList: React.FC<{abilities: IDuelRoundAbility[], total: number, playerClass?: string, color: string}> = ({abilities, total, playerClass, color}) => {
  const classes = useStyles();
  if(abilities.length == 0)
    return <div className={classes.none}>None</div>;
  const top = Math.max(...abilities.map((a) => a.value || 0), 1);
  return (
    <React.Fragment>
      {abilities.map((ability) => {
        let percentage = total > 0 ? Math.round(((ability.value || 0) / total) * 100) : 0;
        return (
          <div key={ability.ability} className={classes.ability}>
            <div className={classes.abilityBar} style={{width: `${Math.round((ability.value || 0) / top * 100)}%`, background: color}} />
            <img className={classes.abilityIcon} src={getAbility(ability.ability || "", playerClass || "")} title={ability.ability} />
            <span className={classes.abilityName} title={ability.ability}>
              {ability.ability}{ability.count ? <span className={classes.abilityCount}>×{ability.count}</span> : null}
            </span>
            <span className={classes.abilityValue}>{(ability.value || 0).toLocaleString()}<span className={classes.abilityPct}>{percentage}%</span></span>
          </div>
        );
      })}
    </React.Fragment>
  );
}

const RoundPlayer: React.FC<{player: IDuelRoundPlayer, won: boolean, color: string}> = ({player, won, color}) => {
  const classes = useStyles();
  const totals = [
    {label: 'Damage', value: (player.damage || 0).toLocaleString()},
    {label: 'Taken', value: (player.damageTaken || 0).toLocaleString()},
    {label: 'Healing', value: (player.heal || 0).toLocaleString()},
    {label: 'CCs', value: player.ccs || 0},
  ];
  return (
    <div className={classes.player} style={{boxShadow: `inset 0 3px 0 ${color}`}}>
      <div className={classes.playerHead}>
        <img className={classes.classIcon} src={classImages.getColorIcon(player.class as AocClasses)} title={player.class} />
        <div style={{minWidth: 0}}>
          <div className={classes.playerName}>{player.name}</div>
          <div className={classes.playerClass}>{player.class}</div>
        </div>
        {won ? <span className={classes.wonTag}>Won round</span> : <span />}
      </div>
      <div className={classes.totals}>
        {totals.map((t) => (
          <div key={t.label} className={classes.total}>
            <div className={classes.totalValue}>{t.value}</div>
            <div className={classes.totalLabel}>{t.label}</div>
          </div>
        ))}
      </div>
      <div className={classes.sectionTitle}>Damage</div>
      <AbilityList abilities={player.damageAbilities || []} total={player.damage || 0} playerClass={player.class} color={color} />
      <div className={classes.sectionTitle}>Healing</div>
      <AbilityList abilities={player.healAbilities || []} total={player.heal || 0} playerClass={player.class} color="#66bb6a" />
      <div className={classes.sectionTitle}>Crowd Control</div>
      {(player.ccAbilities || []).length == 0 ?
        <div className={classes.none}>None</div>
      :
        <div className={classes.ccs}>
          {(player.ccAbilities || []).map((cc) => (
            <span key={cc.ability} className={classes.cc}><span>{cc.ability}</span><b>×{cc.count}</b></span>
          ))}
        </div>
      }
    </div>
  );
}

interface Props {
  round: IDuelRound;
  /** Player names in the order the duel lists them, so colours stay consistent across rounds. */
  playerOrder: string[];
}

/** Everything both players did from the start of a round until the death that ended it. */
export const DuelRoundBreakdown: React.FC<Props> = ({round, playerOrder}) => {
  const classes = useStyles();
  let players = [...(round.players || [])].sort((a, b) => playerOrder.indexOf(a.name || "") - playerOrder.indexOf(b.name || ""));
  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <span className={classes.roundNumber}>Round {round.round}</span>
        <span className={classes.result}><b>{round.winner}</b> defeated <b>{round.loser}</b></span>
        <span className={classes.duration}>{formatDuration(round.duration)}</span>
      </div>
      <div className={classes.players}>
        {players.map((player) => {
          let index = playerOrder.indexOf(player.name || "");
          return <RoundPlayer key={player.name} player={player} won={player.name == round.winner} color={TeamColors[index >= 0 ? index : 0]} />;
        })}
      </div>
    </div>
  );
}
